/**
 * Variant Media Filter - OPTIMIZED VERSION
 * 
 * Performance optimizations:
 * - Pre-compute all stems on init (no re-calculation on variant change)
 * - Cache filter data and DOM references
 * - Direct input event listeners (no setTimeout delays)
 * - Batch DOM updates with requestAnimationFrame
 * - Use CSS classes for instant visual updates
 */

// Enable flicker prevention CSS
document.documentElement.classList.add('ag-media-filter-enabled');

(function() {
  'use strict';

  // ============================================
  // CACHE STORAGE (per gallery)
  // ============================================
  const galleryCache = new WeakMap();

  /**
   * Normalize URL to STEM (cached version uses pre-computed stems)
   */
  function normalize(url) {
    if (!url) return '';
    try {
      const urlNoQuery = url.split('?')[0];
      const basename = (urlNoQuery.split('/').pop() || '').toLowerCase();
      
      // Remove Shopify size suffix: _100x100, _1024x1024, etc.
      const sizeSuffixMatch = basename.match(/^(.+?)_(\d+x\d*|\d*x\d+)(\.[^.]+)?$/);
      const basenameNoSize = sizeSuffixMatch 
        ? sizeSuffixMatch[1] + (sizeSuffixMatch[3] || '')
        : basename;
      
      // Remove extension
      const dotIndex = basenameNoSize.lastIndexOf('.');
      return dotIndex > 0 ? basenameNoSize.substring(0, dotIndex) : basenameNoSize;
    } catch (e) {
      return '';
    }
  }

  /**
   * Handleize string for color key matching
   */
  function handleize(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Build cache for a gallery (called once on init)
   */
  function buildCache(gallery) {
    const cache = {
      filterData: null,
      mediaElements: [],
      mediaStems: [],
      thumbnails: [],
      dots: [],
      zoomThumbnails: [],
      zoomItems: [],
      slideshow: null,
      container: null,
      optionPosition: 0,
      // Pre-computed stem sets for each color
      colorStemSets: {},
      globalStems: new Set()
    };

    // Find and parse JSON data
    const jsonScript = gallery.previousElementSibling?.matches?.('script[data-ag-media-filter]')
      ? gallery.previousElementSibling
      : gallery.parentElement?.querySelector('script[data-ag-media-filter]');

    if (!jsonScript?.textContent) {
      return null;
    }

    try {
      cache.filterData = JSON.parse(jsonScript.textContent);
    } catch (e) {
      return null;
    }

    if (!cache.filterData?.optionPosition) {
      return null;
    }

    cache.optionPosition = cache.filterData.optionPosition;
    cache.container = gallery.closest('.shopify-section, quick-add-dialog, #quick-add-modal-content') || gallery.parentElement;

    // Pre-compute global stems
    if (Array.isArray(cache.filterData.globalAllowed)) {
      cache.filterData.globalAllowed.forEach(url => {
        const stem = normalize(url);
        if (stem) cache.globalStems.add(stem);
      });
    }

    // Pre-compute color group stems
    if (cache.filterData.colorGroups) {
      Object.entries(cache.filterData.colorGroups).forEach(([colorKey, urls]) => {
        const stemSet = new Set(cache.globalStems); // Include global in each color
        if (Array.isArray(urls)) {
          urls.forEach(url => {
            const stem = normalize(url);
            if (stem) stemSet.add(stem);
          });
        }
        cache.colorStemSets[colorKey] = stemSet;
      });
    }

    // Cache media elements and pre-compute their stems
    const slides = gallery.querySelectorAll('slideshow-slide');
    const gridItems = gallery.querySelectorAll('.media-gallery__grid > li[ref="media[]"]');
    
    cache.mediaElements = slides.length > 0 ? Array.from(slides) : Array.from(gridItems);
    
    // Pre-compute stems for each media element
    cache.mediaStems = cache.mediaElements.map(el => {
      if (el.dataset.mediaType !== 'image') return null; // Non-image always visible
      return normalize(el.dataset.mediaUrl);
    });

    // Cache control elements
    cache.thumbnails = Array.from(gallery.querySelectorAll('.slideshow-controls__thumbnail'));
    cache.dots = Array.from(gallery.querySelectorAll('button[ref="dots[]"], .slideshow-controls__dot'));
    cache.zoomThumbnails = Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
    cache.zoomItems = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery li[ref="media[]"]'));
    cache.slideshow = gallery.refs?.slideshow || gallery.querySelector('slideshow');

    return cache;
  }

  /**
   * Get selected color value (optimized)
   */
  function getSelectedColor(container, optionPosition) {
    if (!optionPosition || !container) return null;
    
    const variantPicker = container.querySelector('variant-picker');
    if (!variantPicker) return null;

    // Fast path: radio buttons (most common)
    const checkedRadio = variantPicker.querySelector(
      `input[type="radio"][name*="option${optionPosition}"]:checked, ` +
      `input[type="radio"][data-option-position="${optionPosition}"]:checked, ` +
      `input[data-input-id^="${optionPosition}-"]:checked`
    );
    
    if (checkedRadio) {
      // Try to get label text for more accurate matching
      const label = variantPicker.querySelector(`label[for="${checkedRadio.id}"]`);
      return label?.textContent?.trim() || checkedRadio.value || checkedRadio.dataset.value;
    }

    // Fallback: select dropdown
    const select = variantPicker.querySelector(
      `select[name*="option${optionPosition}"], select[data-option-position="${optionPosition}"]`
    );
    if (select?.selectedIndex >= 0) {
      return select.options[select.selectedIndex].value;
    }

    return null;
  }

  /**
   * Apply filter using cached data (FAST)
   */
  function applyFilterFast(gallery, cache) {
    if (!cache) {
      gallery.dataset.filterReady = '1';
      return;
    }

    const selectedColorRaw = getSelectedColor(cache.container, cache.optionPosition);
    
    if (!selectedColorRaw) {
      showAll(cache);
      gallery.dataset.filterReady = '1';
      return;
    }

    const selectedKey = handleize(selectedColorRaw);
    const allowedStems = cache.colorStemSets[selectedKey];

    // No mapping for this color = show all
    if (!allowedStems || allowedStems.size === 0) {
      showAll(cache);
      gallery.dataset.filterReady = '1';
      return;
    }

    // FAST: Apply visibility using pre-computed data
    let firstVisibleIndex = -1;
    let visibleCount = 0;

    // Single loop with direct array access
    const len = cache.mediaElements.length;
    for (let i = 0; i < len; i++) {
      const el = cache.mediaElements[i];
      const stem = cache.mediaStems[i];
      
      // Non-image (stem is null) = always visible
      if (stem === null) {
        el.classList.remove('ag-hide-media');
        if (firstVisibleIndex === -1) firstVisibleIndex = i;
        visibleCount++;
        continue;
      }

      const isAllowed = allowedStems.has(stem);
      el.classList.toggle('ag-hide-media', !isAllowed);
      
      if (isAllowed) {
        if (firstVisibleIndex === -1) firstVisibleIndex = i;
        visibleCount++;
      }
    }

    // Fail-safe
    if (visibleCount === 0) {
      showAll(cache);
      gallery.dataset.filterReady = '1';
      return;
    }

    // Update controls (optimized batch)
    updateControlsFast(cache);

    // Update slideshow position
    if (firstVisibleIndex >= 0 && cache.slideshow?.select) {
      try {
        cache.slideshow.select(firstVisibleIndex, undefined, { animate: false });
      } catch (e) {}
    }

    gallery.dataset.filterReady = '1';
  }

  /**
   * Show all media (using cache)
   */
  function showAll(cache) {
    cache.mediaElements.forEach(el => el.classList.remove('ag-hide-media'));
    
    const showControl = el => {
      el.classList.remove('ag-hide-media');
      el.removeAttribute('tabindex');
      el.removeAttribute('aria-hidden');
    };
    
    cache.thumbnails.forEach(showControl);
    cache.dots.forEach(showControl);
    cache.zoomThumbnails.forEach(showControl);
    cache.zoomItems.forEach(el => el.classList.remove('ag-hide-media'));
  }

  /**
   * Update control visibility (optimized)
   */
  function updateControlsFast(cache) {
    const len = cache.mediaElements.length;
    
    for (let i = 0; i < len; i++) {
      const isHidden = cache.mediaElements[i].classList.contains('ag-hide-media');
      
      // Thumbnails
      if (cache.thumbnails[i]) {
        cache.thumbnails[i].classList.toggle('ag-hide-media', isHidden);
        if (isHidden) {
          cache.thumbnails[i].setAttribute('tabindex', '-1');
          cache.thumbnails[i].setAttribute('aria-hidden', 'true');
        } else {
          cache.thumbnails[i].removeAttribute('tabindex');
          cache.thumbnails[i].removeAttribute('aria-hidden');
        }
      }
      
      // Dots
      if (cache.dots[i]) {
        cache.dots[i].classList.toggle('ag-hide-media', isHidden);
        if (isHidden) {
          cache.dots[i].setAttribute('tabindex', '-1');
          cache.dots[i].setAttribute('aria-hidden', 'true');
        } else {
          cache.dots[i].removeAttribute('tabindex');
          cache.dots[i].removeAttribute('aria-hidden');
        }
      }
      
      // Zoom thumbnails
      if (cache.zoomThumbnails[i]) {
        cache.zoomThumbnails[i].classList.toggle('ag-hide-media', isHidden);
        if (isHidden) {
          cache.zoomThumbnails[i].setAttribute('tabindex', '-1');
          cache.zoomThumbnails[i].setAttribute('aria-hidden', 'true');
        } else {
          cache.zoomThumbnails[i].removeAttribute('tabindex');
          cache.zoomThumbnails[i].removeAttribute('aria-hidden');
        }
      }
      
      // Zoom items
      if (cache.zoomItems[i]) {
        cache.zoomItems[i].classList.toggle('ag-hide-media', isHidden);
      }
    }
  }

  /**
   * Initialize gallery with caching
   */
  function initGallery(gallery) {
    if (gallery.dataset.filterInitialized === 'true') return;
    gallery.dataset.filterInitialized = 'true';

    const cache = buildCache(gallery);
    
    if (!cache) {
      // No filter data = show all immediately
      gallery.dataset.filterReady = '1';
      return;
    }

    galleryCache.set(gallery, cache);

    // Apply initial filter
    applyFilterFast(gallery, cache);

    // DIRECT INPUT LISTENERS (no delay, instant response)
    setupDirectListeners(gallery, cache);
  }

  /**
   * Instantly update the displayed color name (no server wait)
   */
  function updateColorNameInstantly(container, optionPosition, colorValue) {
    if (!container || !optionPosition || !colorValue) return;

    // Find all elements that display the selected color name
    const selectors = [
      `.option-selector__selected-value`,
      `.variant-option__selected-value`,
      `[data-selected-value]`,
      `.selected-option-value`,
      `legend .current-value`,
      `fieldset legend span:last-child`
    ];

    // Try to find the correct fieldset/option group for this position
    const variantPicker = container.querySelector('variant-picker');
    if (!variantPicker) return;

    const fieldsets = variantPicker.querySelectorAll('fieldset');
    const targetFieldset = fieldsets[optionPosition - 1]; // 0-indexed

    if (targetFieldset) {
      // Update selected value display in this fieldset
      selectors.forEach(selector => {
        const el = targetFieldset.querySelector(selector);
        if (el && el.textContent !== colorValue) {
          el.textContent = colorValue;
        }
      });

      // Also check for legend with span pattern
      const legend = targetFieldset.querySelector('legend');
      if (legend) {
        const valueSpan = legend.querySelector('span:last-of-type, .selected-value');
        if (valueSpan && valueSpan.textContent !== colorValue) {
          valueSpan.textContent = colorValue;
        }
      }
    }

    // Also update any global selected value displays
    const globalSelectors = [
      `.product-option[data-option-position="${optionPosition}"] .selected-value`,
      `[data-option-index="${optionPosition - 1}"] .selected-value`
    ];
    
    globalSelectors.forEach(selector => {
      const el = container.querySelector(selector);
      if (el && el.textContent !== colorValue) {
        el.textContent = colorValue;
      }
    });
  }

  /**
   * Instantly update button/swatch visual states (no server wait)
   */
  function updateSwatchStatesInstantly(container, clickedInput) {
    if (!container || !clickedInput) return;

    const fieldset = clickedInput.closest('fieldset');
    if (!fieldset) return;

    // Get all inputs in this fieldset
    const inputs = fieldset.querySelectorAll('input[type="radio"]');
    
    inputs.forEach(input => {
      const label = fieldset.querySelector(`label[for="${input.id}"]`);
      const wrapper = input.closest('.option-selector__option, .swatch-wrapper, .variant-option');
      
      if (input === clickedInput) {
        // This is the selected one
        input.checked = true;
        input.setAttribute('aria-checked', 'true');
        if (label) {
          label.classList.add('selected', 'is-selected');
          label.setAttribute('aria-checked', 'true');
        }
        if (wrapper) {
          wrapper.classList.add('selected', 'is-selected');
        }
      } else {
        // Deselect others
        input.removeAttribute('aria-checked');
        if (label) {
          label.classList.remove('selected', 'is-selected');
          label.removeAttribute('aria-checked');
        }
        if (wrapper) {
          wrapper.classList.remove('selected', 'is-selected');
        }
      }
    });

    // Force repaint to ensure visual update
    fieldset.offsetHeight;
  }

  /**
   * Setup direct input listeners for instant updates
   */
  function setupDirectListeners(gallery, cache) {
    if (!cache.container) return;

    const variantPicker = cache.container.querySelector('variant-picker');
    if (!variantPicker) return;

    // Listen to ALL input changes in variant picker
    variantPicker.addEventListener('change', (e) => {
      const input = e.target;
      if (input.type === 'radio' || input.tagName === 'SELECT') {
        // Get the color value from the input
        const label = variantPicker.querySelector(`label[for="${input.id}"]`);
        const colorValue = label?.textContent?.trim() || input.value;
        
        // INSTANT: Update color name display
        updateColorNameInstantly(cache.container, cache.optionPosition, colorValue);
        
        // INSTANT: Update swatch visual states
        updateSwatchStatesInstantly(cache.container, input);
        
        // INSTANT: Apply media filter
        applyFilterFast(gallery, cache);
      }
    }, { passive: true });

    // Also listen for click on swatches (capture phase for earliest possible response)
    variantPicker.addEventListener('click', (e) => {
      const label = e.target.closest('label[for]');
      if (label) {
        const inputId = label.getAttribute('for');
        const input = variantPicker.querySelector(`#${inputId}`);
        
        if (input && input.type === 'radio') {
          const colorValue = label.textContent?.trim() || input.value;
          
          // INSTANT: Update everything before browser processes the click
          updateColorNameInstantly(cache.container, cache.optionPosition, colorValue);
          updateSwatchStatesInstantly(cache.container, input);
          
          // Use microtask to ensure radio is checked, then filter
          queueMicrotask(() => {
            applyFilterFast(gallery, cache);
          });
        }
      }
    }, { capture: true, passive: true });
  }

  /**
   * Handle variant:update event (backup, in case direct listeners miss something)
   */
  function handleVariantUpdate(event) {
    const section = event.target?.closest?.('.shopify-section, quick-add-dialog');
    if (!section) return;

    const gallery = section.querySelector('media-gallery');
    if (!gallery) return;

    let cache = galleryCache.get(gallery);
    
    // If no cache, gallery might have been replaced - reinitialize
    if (!cache) {
      delete gallery.dataset.filterInitialized;
      initGallery(gallery);
      return;
    }

    // Apply filter immediately (no setTimeout!)
    applyFilterFast(gallery, cache);
  }

  /**
   * Observe container for new galleries
   */
  function observeContainer(container) {
    if (!container || container.dataset.agFilterObserved === 'true') return;
    container.dataset.agFilterObserved = 'true';

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          
          if (node.tagName === 'MEDIA-GALLERY') {
            // Small delay to ensure JSON script is also in DOM
            requestAnimationFrame(() => initGallery(node));
          } else if (node.querySelector) {
            const galleries = node.querySelectorAll('media-gallery');
            if (galleries.length > 0) {
              requestAnimationFrame(() => {
                galleries.forEach(g => initGallery(g));
              });
            }
          }
        }
      }
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  /**
   * Main initialization
   */
  function init() {
    // Initialize existing galleries
    document.querySelectorAll('media-gallery').forEach(gallery => {
      initGallery(gallery);
    });

    // Backup listener for variant:update
    document.addEventListener('variant:update', handleVariantUpdate);

    // Observe sections
    document.querySelectorAll('.shopify-section:has(media-gallery)').forEach(section => {
      observeContainer(section);
    });

    // Quick-add modal observer
    const quickAddModal = document.getElementById('quick-add-modal-content');
    if (quickAddModal) {
      observeContainer(quickAddModal);
    } else {
      // Watch for modal creation
      const bodyObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== Node.ELEMENT_NODE) continue;
            
            const modal = node.id === 'quick-add-modal-content' 
              ? node 
              : node.querySelector?.('#quick-add-modal-content');
            
            if (modal) {
              observeContainer(modal);
              modal.querySelectorAll('media-gallery').forEach(g => initGallery(g));
            }
          }
        }
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Fallback timeout (safety net)
    setTimeout(() => {
      document.querySelectorAll('media-gallery[data-filter-ready="0"]').forEach(gallery => {
        gallery.dataset.filterReady = '1';
        const cache = galleryCache.get(gallery);
        if (cache) showAll(cache);
      });
    }, 2000);
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
